/**
 * idioma.tsx — el idioma activo, y todo lo que depende de él.
 *
 * El producto es bilingüe de raíz. Eso significa que el idioma no es una
 * preferencia de presentación: decide cómo se ESCRIBE un número y cómo se LEE
 * uno tecleado. Por eso `leerMonto` y `monto` salen de acá ya atados al idioma
 * activo — para que ningún componente tenga que acordarse de pasárselo, que es
 * la forma en que un campo se queda con el idioma equivocado.
 *
 * ⚠️ Lo que el idioma NO cambia nunca es el valor canónico. En los mockups,
 * conmutar de idioma con montos escritos en pantalla los reinterpretaba, y
 * `530.00` pasaba a valer 53 000. Acá el canónico es texto y no depende del
 * idioma: solo su presentación lo hace.
 *
 * Las reglas están en `idioma.ts`, que se prueba sin navegador. Acá solo hay
 * cableado de React.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  LOCALES, formatearFecha, partirEnfasis, sinEnfasis, traducir,
  type Clave, type Idioma,
} from './idioma.ts';
import {
  leer as leerImporte, mostrar as mostrarImporte, mostrarOptativo,
  type Importe,
} from './dinero.ts';

export type { Idioma, Clave };

const LLAVE_GUARDADA = 'sb-idioma';

interface Contexto {
  readonly idioma: Idioma;
  readonly local: (typeof LOCALES)[Idioma];
  cambiar(a: Idioma): void;
  /**
   * Lo que la EMPRESA propone para quien todavía no eligió.
   *
   * ▶ Y la diferencia con `cambiar` es toda la función: `cambiar` es una
   * persona decidiendo —se guarda y manda desde entonces—; esto es un punto de
   * partida. Si alguien ya tocó el conmutador alguna vez, esto no hace nada.
   *
   * Imponerlo sería peor que no tenerlo: un contador que puso la aplicación en
   * inglés la vería volver al español cada vez que entra, sin saber por qué.
   */
  proponer(a: Idioma): void;
  t(clave: Clave, sustituciones?: Readonly<Record<string, string>>): string;
  /** `mostrar` del módulo de dinero, ya atado al idioma activo. */
  monto(importe: Importe): string;
  /** Un importe sin moneda sale como raya, nunca con un símbolo inventado. */
  montoOptativo(importe: Importe | null | undefined): string;
  /** Devuelve TEXTO canónico o `null`. Nunca un número. */
  leerMonto(escrito: string): string | null;
  fecha(iso: string): string;
}

const Ctx = createContext<Contexto | null>(null);

/**
 * Lo que esta persona eligió, o `null` si NO ha elegido.
 *
 * ▶ Devolver `null` y no `'es'` es lo que hace posible el idioma de la empresa.
 * Con el español por omisión, «eligió español» y «no eligió» se veían iguales, y
 * una propuesta de la empresa no tendría cómo distinguirlos.
 */
function elGuardado(): Idioma | null {
  try {
    const v = localStorage.getItem(LLAVE_GUARDADA);
    if (v === 'es' || v === 'en') return v;
  } catch {
    // Navegación privada o almacenamiento bloqueado. No es un fallo: se arranca
    // en español y la sesión funciona igual.
  }
  return null;
}

export function ProveedorDeIdioma({ children }: { children: ReactNode }) {
  const [elegido, setElegido] = useState<Idioma | null>(elGuardado);
  const [propuesto, setPropuesto] = useState<Idioma | null>(null);

  // Lo elegido manda sobre lo propuesto, y el español es el suelo. Los tres
  // estados existen a propósito: sin el del medio no habría forma de que la
  // empresa proponga sin pisar a quien ya decidió.
  const idioma: Idioma = elegido ?? propuesto ?? 'es';

  const cambiar = useCallback((a: Idioma) => {
    setElegido(a);
    try { localStorage.setItem(LLAVE_GUARDADA, a); } catch { /* ver elGuardado */ }
  }, []);

  // No se guarda: si se guardara, el idioma de la empresa pasaría a ser una
  // elección de la persona y dejaría de poder cambiarse desde la configuración.
  const proponer = useCallback((a: Idioma) => setPropuesto(a), []);

  useEffect(() => {
    // `lang` no es cosmético: de él dependen el corte de palabras, el corrector
    // ortográfico y lo que pronuncia un lector de pantalla.
    document.documentElement.lang = LOCALES[idioma];
  }, [idioma]);

  const valor = useMemo<Contexto>(() => ({
    idioma,
    local: LOCALES[idioma],
    cambiar,
    proponer,
    // Texto plano SIEMPRE: los énfasis los dibuja <Texto>, y así `t()` no
    // puede pintar un asterisco en un atributo ni en una pantalla.
    t: (clave, sustituciones) => sinEnfasis(traducir(idioma, clave, sustituciones)),
    monto: (importe) => mostrarImporte(importe, idioma),
    montoOptativo: (importe) => mostrarOptativo(importe, idioma),
    leerMonto: (escrito) => leerImporte(escrito, idioma),
    fecha: (iso) => formatearFecha(iso, idioma),
  }), [idioma, cambiar]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useIdioma(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useIdioma fuera del ProveedorDeIdioma');
  return c;
}

/** Muestra un texto del diccionario respetando los *énfasis*, sin inyectar HTML. */
export function Texto({ k, con }: { k: Clave; con?: Readonly<Record<string, string>> }) {
  const { t } = useIdioma();
  return (
    <>
      {partirEnfasis(t(k, con)).map((p, i) =>
        p.fuerte ? <strong key={i}>{p.texto}</strong> : <span key={i}>{p.texto}</span>,
      )}
    </>
  );
}
